/**
 * Reports: the templates the caller owns, the ones shared with them, and the
 * ones published to the tenant. Viewing only; reports are built and changed in chat.
 */
import {
  Badge,
  Banner,
  Skeleton,
  Table,
  type BadgeVariant,
} from '@servicecompressionllc/sc-design-system';
import { ChevronRight, Search } from '@servicecompressionllc/sc-design-system/icons';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../../components/AppShell.js';
import { useMe } from '../../hooks/useMe.js';
import {
  REPORT_TABS,
  coversLine,
  useReportsPageLogic,
  type ReportTab,
  type ReportTemplateRow,
} from './useReportsPageLogic.js';

const accessOf = (t: ReportTemplateRow, meId: string): { label: string; variant: BadgeVariant } =>
  t.visibility === 'tenant'
    ? { label: 'Published', variant: 'success' }
    : t.owner.id === meId
      ? { label: 'Private', variant: 'neutral' }
      : { label: 'Shared with you', variant: 'info' };

const emptyText = (tab: ReportTab, tenantName: string): string =>
  ({
    mine: "You haven't saved any reports yet. Ask the assistant in chat to build one.",
    shared: 'Nothing has been shared with you yet.',
    published: `No reports are published to ${tenantName} yet.`,
  })[tab];

export const ReportsPage = () => {
  const me = useMe();
  const meId = me.data?.user.id;
  const tenantName = me.data?.tenant.name ?? 'your organization';
  const logic = useReportsPageLogic(meId);
  const navigate = useNavigate();
  const open = (id: string) => navigate(`/reports/templates/${id}`);

  return (
    <AppShell>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ink">Reports</h2>
        <p className="text-sm text-ink-muted">
          Reports you built, reports shared with you, and reports published to {tenantName}. Build
          or change a report by asking the assistant in chat.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-outline">
        <div className="flex gap-1" role="tablist" aria-label="Reports">
          {REPORT_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={logic.tab === t.id}
              onClick={() => logic.setTab(t.id)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                logic.tab === t.id
                  ? 'border-primary text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <label className="mb-2 flex w-[280px] items-center gap-2 rounded border border-outline-strong bg-surface px-3 py-2 text-ink-subtle">
          <Search size={15} aria-hidden />
          <input
            type="search"
            placeholder="Search reports"
            aria-label="Search reports"
            value={logic.search}
            onChange={(e) => logic.setSearch(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none"
          />
        </label>
      </div>

      {logic.error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{logic.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {logic.isLoading && <Skeleton className="h-48 w-full" />}

      {!logic.isLoading && !logic.error && logic.rows.length === 0 && (
        <div className="rounded-lg border border-outline bg-surface-elevated py-12 text-center">
          <p className="text-ink-muted">
            {logic.search.trim()
              ? `No reports match "${logic.search.trim()}".`
              : emptyText(logic.tab, tenantName)}
          </p>
        </div>
      )}

      {logic.rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-outline">
          <Table.Root className="w-full text-sm">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Report</Table.HeaderCell>
                <Table.HeaderCell>Owner</Table.HeaderCell>
                <Table.HeaderCell>Version</Table.HeaderCell>
                <Table.HeaderCell>Access</Table.HeaderCell>
                <Table.HeaderCell aria-hidden />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {logic.rows.map((t) => {
                const access = accessOf(t, meId ?? '');
                return (
                  <Table.Row
                    key={t.id}
                    role="link"
                    tabIndex={0}
                    aria-label={t.name}
                    onClick={() => open(t.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        open(t.id);
                      }
                    }}
                    className="cursor-pointer hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
                  >
                    <Table.Cell>
                      <div className="font-medium text-ink">{t.name}</div>
                      <div className="mt-0.5 text-xs text-ink-subtle">{coversLine(t)}</div>
                    </Table.Cell>
                    <Table.Cell>
                      {t.owner.name ?? 'Unknown'}
                      {t.owner.id === meId && <span className="text-ink-subtle"> (you)</span>}
                    </Table.Cell>
                    <Table.Cell className="text-ink-muted">{t.version}</Table.Cell>
                    <Table.Cell>
                      <Badge variant={access.variant}>{access.label}</Badge>
                    </Table.Cell>
                    <Table.Cell align="right">
                      <ChevronRight size={16} aria-hidden className="inline text-ink-subtle" />
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        </div>
      )}
    </AppShell>
  );
};
